// Geolocation for the two live demos (Multiconexão routes, Multi Internet providers) and the "3 vitórias"
// card that mirrors the Multiconexão routes.
//
// GEO_ENDPOINT is the single place the page would ask "where is this visitor": GET, JSON
// `{ country, region, city, lat?, lon?, isp? }` (alternate key names are accepted by `normalize*`). It is
// a placeholder on purpose, exactly as in the approved design: empty means no request at all, and both
// demos fall back to the United States profile (plan B). QA can force a location with
// `?mcgeo=BR|MG|Belo Horizonte` (country|region|city|lat|lon) and an ISP with `&mcisp=Vivo`.
//
// The country/city/provider tables (~53 KB) are not bundled: they are fetched once, when the first
// block that needs them comes near the viewport (or at idle after `load`), and shared by every caller.

export const GEO_ENDPOINT = '';
const GEO_TIMEOUT = 2500;
export const GEO_DATA_URL = '/assets/images/trial/v3b/geo-data.json';

/** [name, IATA-like code, lat, lon] */
export type GeoCity = [string, string, number, number];
/** [country name (pt), hub city name, cities] */
export type GeoCountry = [string, string, GeoCity[]];
/** fixed: [brand, kind]; mobile: [brand, has5G] */
export interface GeoProviders {
  fixed?: [string, string][];
  mobile?: [string, boolean][];
}

export interface GeoData {
  countries: Record<string, GeoCountry>;
  regions: Record<string, Record<string, string>>;
  aliases: Record<string, string>;
  providers: Record<string, GeoProviders>;
}

export interface VisitorPlace {
  country: string;
  region: string;
  city: string;
  lat: number | null;
  lon: number | null;
}

export interface VisitorNetwork {
  country: string;
  isp: string;
}

type RawGeo = Record<string, unknown> | null;

let geoDataPromise: Promise<GeoData | null> | null = null;
let visitorPromise: Promise<RawGeo> | null = null;

/** The tables, fetched once per page load; `null` when the file cannot be read (the demos keep their static markup). */
export function loadGeoData(): Promise<GeoData | null> {
  if (!geoDataPromise) {
    geoDataPromise =
      typeof fetch === 'function'
        ? fetch(GEO_DATA_URL, { credentials: 'same-origin' })
            .then((response) => (response.ok ? (response.json() as Promise<GeoData>) : null))
            .catch(() => null)
        : Promise.resolve(null);
  }
  return geoDataPromise;
}

function toNumber(value: unknown): number | null {
  const parsed = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

function countryCodeOf(data: Record<string, unknown>): string | null {
  const { country } = data;
  const code = data.country_code ?? data.countryCode ?? (country && typeof country === 'object' ? (country as { code?: unknown }).code : country);
  if (!code || String(code).length !== 2) return null;
  return String(code).toUpperCase();
}

export function normalizePlace(data: RawGeo): VisitorPlace | null {
  if (!data || typeof data !== 'object') return null;
  const country = countryCodeOf(data);
  if (!country) return null;
  return {
    country,
    region: String(data.region_code ?? data.regionCode ?? data.region ?? data.state ?? ''),
    city: String(data.city ?? ''),
    lat: toNumber(data.lat ?? data.latitude),
    lon: toNumber(data.lon ?? data.lng ?? data.longitude),
  };
}

export function normalizeNetwork(data: RawGeo): VisitorNetwork | null {
  if (!data || typeof data !== 'object') return null;
  const country = countryCodeOf(data);
  if (!country) return null;
  const connection = data.connection as { isp?: unknown; org?: unknown } | undefined;
  const isp = data.isp ?? data.org ?? data.asn_org ?? connection?.isp ?? connection?.org ?? '';
  return { country, isp: String(isp) };
}

function queryParam(name: string): string | null {
  if (typeof window === 'undefined') return null;
  const match = new RegExp(`[?&]${name}=([^&#]*)`).exec(window.location.search);
  if (!match) return null;
  try {
    return decodeURIComponent((match[1] ?? '').replace(/\+/g, ' '));
  } catch {
    return null;
  }
}

/** QA override: `?mcgeo=BR|MG|Belo Horizonte|lat|lon`. */
export function forcedPlace(): VisitorPlace | null {
  const forced = queryParam('mcgeo');
  if (forced === null) return null;
  const [country, region, city, lat, lon] = forced.split('|');
  return normalizePlace({ country, region, city, lat, lon });
}

/** QA override: `?mcgeo=BR&mcisp=Vivo`. */
export function forcedNetwork(): VisitorNetwork | null {
  const forced = queryParam('mcgeo');
  if (forced === null) return null;
  return normalizeNetwork({ country: forced.split('|')[0], isp: queryParam('mcisp') ?? '' });
}

/** One visitor lookup per page, shared by both demos. Resolves `null` with no endpoint, on error or after GEO_TIMEOUT. */
export function askVisitorGeo(): Promise<RawGeo> {
  if (!GEO_ENDPOINT || typeof fetch !== 'function') return Promise.resolve(null);
  if (!visitorPromise) {
    visitorPromise = new Promise<RawGeo>((resolve) => {
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = window.setTimeout(() => {
        controller?.abort();
        resolve(null);
      }, GEO_TIMEOUT);
      fetch(GEO_ENDPOINT, { credentials: 'same-origin', signal: controller?.signal })
        .then((response) => (response.ok ? (response.json() as Promise<RawGeo>) : null))
        .then(
          (data) => {
            window.clearTimeout(timer);
            resolve(data);
          },
          () => {
            window.clearTimeout(timer);
            resolve(null);
          }
        );
    });
  }
  return visitorPromise;
}

/** Test seam: forget the cached requests. */
export function resetGeoCache() {
  geoDataPromise = null;
  visitorPromise = null;
}
